/**
 * pgg-zoom-lightbox.js
 * ------------------------------------------------------------
 * Adds two features to every .pgg 2x2 grid gallery on the page
 * (snippets/product-grid-gallery.liquid):
 *
 *   1. ZOOM LENS (desktop, mouse only)
 *      Hovering a grid image shows a square lens that follows
 *      the cursor, and a magnified crop of that image in a
 *      floating pane next to it — like vstar.in's PDP.
 *
 *   2. LIGHTBOX / POPUP SLIDER (all devices)
 *      Clicking a grid image opens a full-screen viewer with
 *      prev/next arrows, a close button, swipe support on touch,
 *      and arrow-key navigation on desktop.
 *
 * This file is fully independent of product-grid-gallery.js — it
 * only READS the currently-visible cell images at the moment of
 * hover/click, so it automatically stays in sync whenever the
 * grid swaps to a different color (no extra wiring needed).
 *
 * Requires: assets/pgg-zoom-lightbox.css
 * Include on the page (once), after product-grid-gallery.js:
 *   {{ 'pgg-zoom-lightbox.css' | asset_url | stylesheet_tag }}
 *   <script src="{{ 'pgg-zoom-lightbox.js' | asset_url }}" defer="defer"></script>
 * ------------------------------------------------------------
 */
(function () {
  var ZOOM_FACTOR = 2.5; // how much the lens pane magnifies
  var ZOOM_PANE_SIZE = 420; // px, square preview pane size
  var canHover =
    window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ---------------------------------------------------------- */
  /* Helpers                                                     */
  /* ---------------------------------------------------------- */

  // Bump a Shopify CDN image URL's ?width= param up for a
  // crisper zoom / lightbox source. Falls back to the original
  // src untouched if no width param is present.
  function higherRes(src, targetWidth) {
    if (!src) return src;
    if (/[?&]width=\d+/.test(src)) {
      return src.replace(/([?&])width=\d+/, '$1width=' + targetWidth);
    }
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + targetWidth;
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  /* ---------------------------------------------------------- */
  /* 1. Zoom lens                                                */
  /* ---------------------------------------------------------- */

  function initZoom(root) {
    if (!canHover) return; // skip entirely on touch/coarse pointers

    var cells = Array.prototype.slice.call(root.querySelectorAll('.pgg__cell'));
    if (!cells.length) return;

    // One shared floating pane per grid, reused across all 4 cells.
    var pane = document.createElement('div');
    pane.className = 'pgg-zoompane';
    document.body.appendChild(pane);

    cells.forEach(function (cell) {
      var img = cell.querySelector('img');
      if (!img) return;

      var lens = document.createElement('div');
      lens.className = 'pgg-lens';
      cell.appendChild(lens);

      function positionPane() {
        var cellRect = cell.getBoundingClientRect();
        var paneSize = ZOOM_PANE_SIZE;
        var left = cellRect.right + 16;

        // Not enough room to the right → try the left, else overlay
        // centered over the viewport so it's never cut off.
        if (left + paneSize > window.innerWidth) {
          left = cellRect.left - paneSize - 16;
        }
        if (left < 8) {
          left = clamp(cellRect.left, 8, window.innerWidth - paneSize - 8);
        }

        var top = clamp(cellRect.top, 8, window.innerHeight - paneSize - 8);

        pane.style.width = paneSize + 'px';
        pane.style.height = paneSize + 'px';
        pane.style.left = left + 'px';
        pane.style.top = top + 'px';
      }

      function onMove(e) {
        var rect = cell.getBoundingClientRect();
        var x = e.clientX - rect.left;
        var y = e.clientY - rect.top;

        var lensSize = rect.width / ZOOM_FACTOR;
        var lensX = clamp(x - lensSize / 2, 0, rect.width - lensSize);
        var lensY = clamp(y - lensSize / 2, 0, rect.height - lensSize);

        lens.style.width = lensSize + 'px';
        lens.style.height = lensSize + 'px';
        lens.style.left = lensX + 'px';
        lens.style.top = lensY + 'px';

        var bgSrc = higherRes(img.getAttribute('src'), 2000);
        if (pane.getAttribute('data-src') !== bgSrc) {
          pane.style.backgroundImage = 'url("' + bgSrc + '")';
          pane.setAttribute('data-src', bgSrc);
        }

        var bgWidth = rect.width * ZOOM_FACTOR;
        var bgHeight = rect.height * ZOOM_FACTOR;
        pane.style.backgroundSize = bgWidth + 'px ' + bgHeight + 'px';

        var bgPosX = -(lensX * ZOOM_FACTOR);
        var bgPosY = -(lensY * ZOOM_FACTOR);
        pane.style.backgroundPosition = bgPosX + 'px ' + bgPosY + 'px';
      }

      cell.addEventListener('mouseenter', function () {
        cell.classList.add('pgg-cell--zooming');
        pane.classList.add('pgg-zoompane--visible');
        positionPane();
      });

      cell.addEventListener('mousemove', function (e) {
        positionPane();
        onMove(e);
      });

      cell.addEventListener('mouseleave', function () {
        cell.classList.remove('pgg-cell--zooming');
        pane.classList.remove('pgg-zoompane--visible');
      });
    });

    window.addEventListener('scroll', function () {
      pane.classList.remove('pgg-zoompane--visible');
    });
  }

  /* ---------------------------------------------------------- */
  /* 2. Lightbox / popup slider                                  */
  /* ---------------------------------------------------------- */

  var lightbox = null; // singleton, shared by every grid on the page

  function buildLightbox() {
    if (lightbox) return lightbox;

    var overlay = document.createElement('div');
    overlay.className = 'pgg-lightbox';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.innerHTML =
      '<div class="pgg-lightbox__dialog">' +
      '<button type="button" class="pgg-lightbox__close" aria-label="Close">&times;</button>' +
      '<button type="button" class="pgg-lightbox__arrow pgg-lightbox__arrow--prev" aria-label="Previous image">&lsaquo;</button>' +
      '<img class="pgg-lightbox__img" alt="">' +
      '<button type="button" class="pgg-lightbox__arrow pgg-lightbox__arrow--next" aria-label="Next image">&rsaquo;</button>' +
      '<span class="pgg-lightbox__counter"></span>' +
      '</div>';
    document.body.appendChild(overlay);

    var state = { images: [], index: 0 };
    var imgEl = overlay.querySelector('.pgg-lightbox__img');
    var counterEl = overlay.querySelector('.pgg-lightbox__counter');
    var touchStartX = null;

    function render() {
      var current = state.images[state.index];
      if (!current) return;
      imgEl.src = higherRes(current.src, 2000);
      imgEl.alt = current.alt || '';
      counterEl.textContent = state.index + 1 + ' / ' + state.images.length;
    }

    function open(images, startIndex) {
      state.images = images;
      state.index = startIndex || 0;
      render();
      overlay.classList.add('pgg-lightbox--open');
      document.documentElement.style.overflow = 'hidden';
    }

    function close() {
      overlay.classList.remove('pgg-lightbox--open');
      document.documentElement.style.overflow = '';
    }

    function next() {
      if (!state.images.length) return;
      state.index = (state.index + 1) % state.images.length;
      render();
    }

    function prev() {
      if (!state.images.length) return;
      state.index = (state.index - 1 + state.images.length) % state.images.length;
      render();
    }

    overlay.querySelector('.pgg-lightbox__close').addEventListener('click', close);
    overlay.querySelector('.pgg-lightbox__arrow--next').addEventListener('click', next);
    overlay.querySelector('.pgg-lightbox__arrow--prev').addEventListener('click', prev);

    // Click on the dark backdrop (outside the dialog image area) closes it
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) close();
    });

    document.addEventListener('keydown', function (e) {
      if (!overlay.classList.contains('pgg-lightbox--open')) return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    });

    // Basic swipe support for touch devices
    overlay.addEventListener(
      'touchstart',
      function (e) {
        touchStartX = e.touches[0].clientX;
      },
      { passive: true }
    );
    overlay.addEventListener(
      'touchend',
      function (e) {
        if (touchStartX == null) return;
        var delta = e.changedTouches[0].clientX - touchStartX;
        if (Math.abs(delta) > 40) {
          delta < 0 ? next() : prev();
        }
        touchStartX = null;
      },
      { passive: true }
    );

    lightbox = { open: open, close: close };
    return lightbox;
  }

  function initLightbox(root) {
    var cells = Array.prototype.slice.call(root.querySelectorAll('.pgg__cell'));
    if (!cells.length) return;

    var lb = buildLightbox();

    cells.forEach(function (cell, i) {
      cell.addEventListener('click', function () {
        // Read whatever is on screen right now, so this always
        // matches the currently-selected color's 4 images.
        var currentCells = Array.prototype.slice.call(root.querySelectorAll('.pgg__cell'));
        var images = currentCells.map(function (c) {
          var img = c.querySelector('img');
          return { src: img ? img.currentSrc || img.src : '', alt: img ? img.alt : '' };
        });
        lb.open(images, i);
      });
    });
  }

  /* ---------------------------------------------------------- */
  /* Boot                                                         */
  /* ---------------------------------------------------------- */

  function boot() {
    document.querySelectorAll('.pgg').forEach(function (root) {
      if (root.getAttribute('data-zoom-lightbox-ready') === '1') return;
      root.setAttribute('data-zoom-lightbox-ready', '1');
      initZoom(root);
      initLightbox(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init when merchant edits the section in the theme editor
  document.addEventListener('shopify:section:load', boot);
})();
